import type { Unavailable } from '../v4/api'

/**
 * A refusal that only means "not yet": the organization's files or knowledge graph are still being
 * read, and the same request works in a minute. Shown as a grey hint, not as an error.
 */
export function isStillLoading(u: Unavailable): boolean {
  return u.kind === 'warming' && (u.code === 'engine_still_loading' || u.code === 'kg_still_warming')
}

/** Missing readings are a data prerequisite, not a failed request (Pegah, 2026-11-01).
 * The API currently exposes this tenant-config reason as text; match its exact value so
 * unrelated configuration failures remain alerts. Keep polling's isStillLoading separate.
 */
export function isAvailabilityHint(u: Unavailable): boolean {
  return isStillLoading(u) || (
    u.kind === 'not_available' && u.reason === 'dataCoverage.series: the tenant has no historian readings'
  )
}

/** The bar under a page header: grey for loading/missing data, red for actual failures. */
export const HINT_BAR = 'border-b border-border bg-muted px-6 py-2 text-sm text-muted-foreground dark:bg-dark-muted'
export const ERROR_BAR =
  'border-b border-red-200 bg-red-50 px-6 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-400'

/** Why workflows cannot run here, in the v4 page's words. */
export function unavailableText(u: Unavailable, t: (key: string, opts: { defaultValue: string }) => string): string {
  if (u.kind === 'not_available') {
    const base = t('workflow:builder.unavailable_org', {
      defaultValue: 'Workflows are not available for this organization',
    })
    return u.reason ? `${base}: ${u.reason}` : `${base}.`
  }
  if (u.code === 'engine_still_loading')
    return t('workflow:builder.unavailable_loading', {
      defaultValue: "This organization's files are still being read for workflows. Try again in a minute.",
    })
  if (u.code === 'kg_still_warming')
    return t('workflow:builder.unavailable_kg_warming', {
      defaultValue: "This organization's knowledge graph is still loading. Try again in a minute.",
    })
  return `${t('workflow:builder.unavailable_kg', {
    defaultValue: "This organization's knowledge graph could not be loaded",
  })} (${u.code}).`
}
